import React, { useEffect, useState } from "react";
import "./CookieConsent.css";

import {
  CONSENT_KEY,
  POLICY_VERSION,
  notifyConsentChange,
} from "./cookieConsentUtils";

const DEFAULT_CONSENT = {
  essential: true,
  analytics: false,
  marketing: false,
};

function CookieConsent() {
  const [consent, setConsent] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_KEY);

    if (storedConsent) {
        try {
            const parsedConsent = JSON.parse(storedConsent);

            setConsent(parsedConsent);

            setPreferences({
                essential: true,
                analytics: Boolean(parsedConsent.analytics),
                marketing: Boolean(parsedConsent.marketing),
            });
        } catch (error) {
        console.error("Could not read cookie consent:", error);
        setConsent(null);
        }

        setIsReady(true);
        return;
    }

    // Let the page render first before showing the banner
    const timer = setTimeout(() => {
        setIsReady(true);
    }, 800);

    return () => clearTimeout(timer);
    }, []);

  const saveConsent = (newConsent) => {
    const consentRecord = {
      essential: true,
      analytics: Boolean(newConsent.analytics),
      marketing: Boolean(newConsent.marketing),
      timestamp: new Date().toISOString(),
      policyVersion: POLICY_VERSION,
    };

    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify(consentRecord)
    );

    setConsent(consentRecord);
    setPreferences({
      essential: true,
      analytics: Boolean(newConsent.analytics),
      marketing: Boolean(newConsent.marketing),
    });

    setShowPreferences(false);
    notifyConsentChange(consentRecord);
  };

  const handleAccept = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleReject = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const openPreferences = () => {
    const storedConsent = localStorage.getItem(CONSENT_KEY);

    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(storedConsent);

        setPreferences({
          essential: true,
          analytics: Boolean(parsedConsent.analytics),
          marketing: Boolean(parsedConsent.marketing),
        });
      } catch {
        setPreferences(DEFAULT_CONSENT);
      }
    }

    setShowPreferences(true);
  };

  /*
   * No consent yet:
   * show the main banner.
   */
  if (!isReady) {
     return null;
    }
  
  if (!consent && !showPreferences) {
    return (
      <div className="cookie-banner">
        <div className="cookie-banner-content">
          <h3 className="cookie-banner-title">
            Manage cookies
          </h3>

          <p className="cookie-banner-text">
            We use cookies to provide the best site experience. View our{" "}
              <a href="/privacy-policy">Privacy Policy</a> for more information.
          </p>

          <div className="cookie-banner-actions">
            <button
              type="button"
              className="cookie-button cookie-button-preferences"
              onClick={openPreferences}
            >
              Preferences
            </button>

            <button
              type="button"
              className="cookie-button cookie-button-reject"
              onClick={handleReject}
            >
              Reject
            </button>

            <button
              type="button"
              className="cookie-button cookie-button-accept"
              onClick={handleAccept}
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    );
  }

  /*
   * Preferences panel.
   */
  if (showPreferences) {
    return (
      <div className="cookie-banner cookie-preferences-panel">
        <div className="cookie-preferences-content">
          <h2>Cookie Preferences</h2>

          <p className="cookie-preferences-intro">
            Choose which categories of cookies and tracking you
            want to allow. Essential cookies are always enabled
            because they are required for the website to function.
          </p>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3>Essential</h3>
                <p>
                  Required for authentication, security, session
                  functionality, and core website features.
                </p>
              </div>

              <span className="cookie-always-on">
                Always on
              </span>
            </div>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3>Analytics</h3>
                <p>
                  Website and product analytics, heatmaps, and
                  session-replay tools.
                </p>
              </div>

              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences((prev) => ({
                      ...prev,
                      analytics: e.target.checked,
                    }))
                  }
                />
                <span className="cookie-toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3>Marketing</h3>
                <p>
                  Advertising and marketing technologies such as
                  Meta Pixel, TikTok Pixel, and LinkedIn Insight Tag.
                </p>
              </div>

              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences((prev) => ({
                      ...prev,
                      marketing: e.target.checked,
                    }))
                  }
                />
                <span className="cookie-toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="cookie-preferences-actions">
            <button
              type="button"
              className="cookie-button cookie-button-reject"
              onClick={handleReject}
            >
              Reject All
            </button>

            <button
              type="button"
              className="cookie-button cookie-button-accept"
              onClick={handleSavePreferences}
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    );
  }

  /*
   * Consent already exists:
   * show a small permanent button so the user can change
   * their decision later.
   */
  return (
    <button
      type="button"
      className="cookie-preferences-trigger"
      onClick={openPreferences}
      aria-label="Open cookie preferences"
    >
      Cookie Preferences
    </button>
  );
}

export default CookieConsent;